import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Mirrors GET /api/financials/overview (server/financials-routes.ts).
export interface FinMonthly { month: string; actual: number; backlog: number; upside: number }

/**
 * Workfees and MVAs are advances against the success fee, so `projectedFee` is
 * what is still to COME (remaining draws + the netted closing balance), not the
 * gross fee. `successFee` carries the gross for display.
 */
export interface FinFeeEconomics {
  successFee: number | null;
  workfeeMonthly: number | null;
  workfeeStartOn: string | null;
  workfeeAccrued: number;
  workfeeFuture: number;
  entryCredits: number;
  credits: number;
  balance: number | null;
  overdrawn: boolean;
}
export interface FinPipelineClient extends FinFeeEconomics {
  companyId: string;
  name: string;
  stage: string | null;
  projectedFee: number | null;
  expectedCloseOn: string | null;
  /** Raw inputs, so rows can be edited from the dashboard. */
  dealValue: number | null;
  feePct: number | null;
}
export interface FinPipelineProspect {
  companyId: string;
  name: string;
  stage: string | null;
  successFee: number | null;
  entryCredits: number;
  credits: number;
  overdrawn: boolean;
  projectedFee: number | null;
  expectedCloseOn: string | null;
  starredAt: string;
  dealValue: number | null;
  feePct: number | null;
}
export interface FeeDetail extends FinFeeEconomics {
  feePct: number | null;
  accruedMonths: string[];
  futureMonths: string[];
  projectedFee: number | null;
  expectedCloseOn: string | null;
}
export interface AttributableCompany { id: string; name: string; category: string }
export interface FinEntry { id: string; receivedOn: string; amount: number; type: string; companyId: string | null; companyName: string | null; notes: string | null }

export interface FinancialsOverview {
  year: number;
  statCards: {
    actual: { total: number; closedDeals: number; entriesTotal: number; workfeeAccrued: number; soldMissingFee: number };
    backlog: { total: number; deals: number; missingFee: number };
    upside: { total: number; prospects: number; missingEstimate: number };
  };
  monthly: FinMonthly[];
  unscheduled: { backlog: number; upside: number };
  pastDue: { backlog: number; upside: number };
  mix: { type: string; amount: number }[];
  byClient: { companyId: string; name: string; amount: number; bucket: "backlog" | "upside" }[];
  pipeline: { clients: FinPipelineClient[]; prospects: FinPipelineProspect[] };
  entries: FinEntry[];
}

async function jfetch(url: string, init?: RequestInit) {
  const r = await fetch(url, { credentials: "include", headers: { "Content-Type": "application/json" }, ...init });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `Request failed (${r.status})`);
  return j;
}

export function useFinancialsOverview(year: number) {
  return useQuery<FinancialsOverview>({
    queryKey: ["financials-overview", year],
    queryFn: () => jfetch(`/api/financials/overview?year=${year}`),
    staleTime: 15_000,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
}

/** Fee economics for one engagement (gated). Powers the client detail fee block
 *  and the closing-fee pre-fill, which must be net of everything drawn. */
export function useFeeDetail(companyId: string | undefined, enabled = true) {
  return useQuery<FeeDetail>({
    queryKey: ["financials-fee-detail", companyId],
    queryFn: () => jfetch(`/api/financials/fee-detail/${companyId}`),
    enabled: !!companyId && enabled,
    staleTime: 15_000,
  });
}

export function useAttributableCompanies(enabled = true) {
  return useQuery<AttributableCompany[]>({
    queryKey: ["financials-attributable"],
    queryFn: () => jfetch("/api/financials/attributable"),
    enabled,
    staleTime: 300_000,
  });
}

export function useFinancialsMutations() {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["financials-overview"] });
    // Credits change what lands at close, so the per-client detail moves too.
    qc.invalidateQueries({ queryKey: ["financials-fee-detail"] });
    // Star state renders on the Prospects page too.
    qc.invalidateQueries({ queryKey: ["/api/prospects"] });
  };

  return {
    addEntry: useMutation({
      mutationFn: (body: { receivedOn: string; amount: number; type: string; companyId?: string | null; notes?: string | null }) =>
        jfetch("/api/financials/revenue-entries", { method: "POST", body: JSON.stringify(body) }),
      onSuccess: invalidate,
    }),
    updateEntry: useMutation({
      mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        jfetch(`/api/financials/revenue-entries/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
      onSuccess: invalidate,
    }),
    deleteEntry: useMutation({
      mutationFn: (id: string) => jfetch(`/api/financials/revenue-entries/${id}`, { method: "DELETE" }),
      onSuccess: invalidate,
    }),
    star: useMutation({
      mutationFn: ({ companyId, ...body }: { companyId: string; estimatedDealValue?: number | null; expectedFeePercentage?: number | null; expectedCloseOn?: string | null }) =>
        jfetch(`/api/financials/star/${companyId}`, { method: "POST", body: JSON.stringify(body) }),
      onSuccess: invalidate,
    }),
    unstar: useMutation({
      mutationFn: (companyId: string) => jfetch(`/api/financials/star/${companyId}`, { method: "DELETE" }),
      onSuccess: invalidate,
    }),
    /** Edit the company fields this dashboard depends on, from the dashboard. */
    updateCompany: useMutation({
      mutationFn: ({ companyId, updates }: { companyId: string; updates: Record<string, unknown> }) =>
        jfetch(`/api/companies/${companyId}`, { method: "PATCH", body: JSON.stringify(updates) }),
      onSuccess: (_res, { companyId }) => {
        invalidate();
        qc.invalidateQueries({ queryKey: ["company", companyId] });
        qc.invalidateQueries({ queryKey: ["clients"] });
      },
    }),
  };
}

// Shared formatter for every financial widget. Compact USD: $2.4M / $850K / $500.
export function fmtUsd(x: number): string {
  const neg = x < 0 ? "-" : "";
  const v = Math.abs(x);
  if (v >= 1_000_000) return `${neg}$${(v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1)}M`;
  if (v >= 1_000) return `${neg}$${Math.round(v / 1_000)}K`;
  return `${neg}$${v}`;
}
